import {
  calculateRoomNumbersForMethod,
  type IRoomNumberingMethodKey,
  type IDrammenSykehusCalculatedRoomNumbersByRoomNumber,
  type IDrammenSykehusCalculateRoomNumbersInput,
  type IDrammenSykehusDoorInput,
  type IDrammenSykehusRoomNumberingRoomInput,
} from "@multiconsult-group/multiplacer-core";
import type { IAppThunk, IRootState } from "../app/store";
import {
  openapi,
  type PostMultiplacerRoomsUpdateCalculatedRoomNumbersApiResponse,
} from "../../openapi";
import { selectHttpMcbimSpacesResult } from "../McBim/httpMcBimSelectors";
import { selectHttpGetRoomsCurrentFloorResult } from "../Room/httpRoomSelectors";
import {
  setHeatmapVisible,
  selectRoomNumberingNumberingOrigin,
  selectRoomNumberingRestrictionLines,
  selectRoomNumberingSelectedBuildings,
} from "./roomNumberingSlice";
import { getNrhRoomNumberingCalculationFixedCacheKey } from "./roomNumberingSelectors";

const thunkBuildDrammenSykehusRoomNumberingInput = (
  state: IRootState,
  projectNo: number,
): IDrammenSykehusCalculateRoomNumbersInput => {
  const mcbimSpaces = selectHttpMcbimSpacesResult(state);
  const selectedBuildingSet = new Set(selectRoomNumberingSelectedBuildings(state));
  const roomsByRoomNumber = selectHttpGetRoomsCurrentFloorResult(state);
  const floorName = state.design.floorAndJob?.floorName ?? "";
  const floorNumberMatch = floorName.match(/-(\d+)$/);
  const floorNumber = floorNumberMatch ? parseInt(floorNumberMatch[1]!, 10) : 0;
  const doorItems =
    openapi.endpoints.postMcbimItems.select({
      body: {
        projectNo,
        buildingStoryName: floorName,
        instanceClasses: ["IfcDoor"],
      },
    })(state).data ?? [];

  const doorsOnFloor: IDrammenSykehusDoorInput[] = doorItems.flatMap((door) => {
    if (!door.globalId || !door.bbox || door.bbox.length < 4) {
      return [];
    }

    return [
      {
        globalId: door.globalId,
        bbox: door.bbox,
      },
    ];
  });

  const roomsOnFloor: IDrammenSykehusRoomNumberingRoomInput[] = mcbimSpaces.flatMap((space) => {
    if (selectedBuildingSet.size > 0) {
      const building = space.building?.trim();
      if (building == null || !selectedBuildingSet.has(building)) {
        return [];
      }
    }

    if (!space.name || !space.polygon || space.polygon.length < 4) {
      return [];
    }

    const roomSummary = roomsByRoomNumber[space.name];

    if (!roomSummary) {
      return [];
    }

    return [
      {
        polygon: space.polygon as IDrammenSykehusRoomNumberingRoomInput["polygon"],
        roomName: roomSummary.roomName,
        roomNumber: roomSummary.roomNumber,
      },
    ];
  });

  return {
    roomsOnFloor,
    doors: doorsOnFloor,
    floorNumber,
  };
};

async function thunkCalculateNrhRoomNumbers(
  dispatch: Parameters<IAppThunk>[0],
  getState: Parameters<IAppThunk>[1],
  state: ReturnType<Parameters<IAppThunk>[1]>,
  projectNo: number,
): Promise<PostMultiplacerRoomsUpdateCalculatedRoomNumbersApiResponse> {
  const floorAndJob = state.design.floorAndJob;
  if (!floorAndJob?.jobId || !floorAndJob.floorGlobalId) {
    dispatch(setHeatmapVisible(false));
    return {
      counts: {
        received: 0,
        matched: 0,
        updated: 0,
        unchanged: 0,
        unknown: 0,
      },
      unknownRooms: [],
    };
  }

  const requestBody = {
    projectNo,
    jobId: floorAndJob.jobId,
    floorGlobalId: floorAndJob.floorGlobalId,
    restrictionLines: selectRoomNumberingRestrictionLines(getState()),
    numberingOrigin: selectRoomNumberingNumberingOrigin(getState()),
    selectedBuildings: selectRoomNumberingSelectedBuildings(getState()),
  };

  const response = await dispatch(
    openapi.endpoints.postMultiplacerRoomsRoomNumberingCalculateNrh.initiate({
      body: {
        ...requestBody,
      },
    }, {
      fixedCacheKey: getNrhRoomNumberingCalculationFixedCacheKey(requestBody),
    }),
  ).unwrap();

  dispatch(setHeatmapVisible(response.heatmap.edges.length > 0));

  return response.updateResult;
}

export const thunkCalculateRoomNumbers =
  (
    methodKey: IRoomNumberingMethodKey
  ): IAppThunk<Promise<PostMultiplacerRoomsUpdateCalculatedRoomNumbersApiResponse>> =>
  async (dispatch, getState) => {
    const state = getState();
    const projectNo = state.design.projectNo;

    if (!projectNo) {
      return {
        counts: {
          received: 0,
          matched: 0,
          updated: 0,
          unchanged: 0,
          unknown: 0,
        },
        unknownRooms: [],
      };
    }

    switch (methodKey) {
      case "NRH":
        return thunkCalculateNrhRoomNumbers(dispatch, getState, state, projectNo);
      case "DRAMMEN_SYKEHUS": {
        const roomNumberToCalculatedRoomNumber = calculateRoomNumbersForMethod(
          methodKey,
          thunkBuildDrammenSykehusRoomNumberingInput(state, projectNo),
        ) as IDrammenSykehusCalculatedRoomNumbersByRoomNumber;
        return dispatch(
          openapi.endpoints.postMultiplacerRoomsUpdateCalculatedRoomNumbers.initiate({
            body: {
              projectNo,
              methodKey,
              roomNumberToCalculatedRoomNumber,
            },
          })
        ).unwrap();
      }
      default: {
        const exhaustive: never = methodKey;
        throw new Error(`Unsupported room numbering method: ${exhaustive}`);
      }
    }
  };
